export const CAMPAIGN_UTM_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const

export type CampaignUtmParamKey = typeof CAMPAIGN_UTM_PARAM_KEYS[number]

export type CampaignUtmParams = Record<CampaignUtmParamKey, string>

export const emptyCampaignUtmParams = (): CampaignUtmParams => ({
  utm_source: '',
  utm_medium: '',
  utm_campaign: '',
  utm_content: '',
  utm_term: '',
})

export const CAMPAIGN_UTM_PARAM_LABELS: Record<CampaignUtmParamKey, string> = {
  utm_source: 'UTM Source',
  utm_medium: 'UTM Medium',
  utm_campaign: 'UTM Campaign',
  utm_content: 'UTM Content',
  utm_term: 'UTM Term',
}

const normalizeBaseUrl = (url: string): string => url.replace(/\/$/, '')

const normalizeClientAppUrl = (url: string): string => {
  const trimmed = url.trim()
  if (!trimmed) {
    return trimmed
  }

  try {
    const parsed = new URL(trimmed)
    if (parsed.hostname === '0.0.0.0') {
      parsed.hostname = 'localhost'
    }
    parsed.pathname = ''
    parsed.search = ''
    parsed.hash = ''
    return normalizeBaseUrl(parsed.toString())
  } catch {
    return normalizeBaseUrl(trimmed)
  }
}

export const deriveClientAppUrlFromApiBaseUrl = (apiBaseUrl: string): string | null => {
  const trimmed = apiBaseUrl.trim()
  if (!trimmed) {
    return null
  }

  try {
    const url = new URL(trimmed)

    if (url.hostname.startsWith('api.')) {
      url.hostname = url.hostname.slice(4)
      url.pathname = ''
      url.search = ''
      url.hash = ''
      return normalizeBaseUrl(url.toString())
    }

    const isLocalApiHost =
      url.hostname === '0.0.0.0' || url.hostname === 'localhost' || url.hostname === '127.0.0.1'

    if (isLocalApiHost && (url.port === '4000' || url.port === '')) {
      return 'http://localhost:3000'
    }
  } catch {
    return null
  }

  return null
}

export const getClientAppUrl = (): string => {
  const explicit =
    process.env.REACT_APP_CLIENT_URL?.trim() || process.env.REACT_APP_GBC_CLIENT_BASE_URL?.trim()

  if (explicit) {
    return normalizeClientAppUrl(explicit)
  }

  const derived = deriveClientAppUrlFromApiBaseUrl(process.env.REACT_APP_GBC_BASE_URL ?? '')
  if (derived) {
    return derived
  }

  return 'http://localhost:3000'
}

export const buildMarketingSignupPathLink = (opportunitySlug: string): string =>
  `${getClientAppUrl()}/signup/${opportunitySlug}`

export const buildMarketingSignupQueryLink = (
  opportunitySlug: string,
  utmParams: CampaignUtmParams,
): string => {
  const params = new URLSearchParams()
  params.set('opportunity', opportunitySlug)

  for (const key of CAMPAIGN_UTM_PARAM_KEYS) {
    const value = utmParams[key]?.trim()
    if (value) {
      params.set(key, value)
    }
  }

  return `${getClientAppUrl()}/signup?${params.toString()}`
}
